
import { forwardRef, useEffect, useId, useState } from 'react';
import { Button, Row, Spacer, Stack, Text } from '@client/shared/design';
import { cx } from '@client/shared/lib/cx.ts';
import type { Agent } from '../agent-operations.ts';
import { AnchoredCard } from '@client/shared/design';
import { AssigneeCombobox } from './AssigneeCombobox.tsx';
import { ModeToggle } from './BookingCalendarParts.tsx';
import { BlockConfirmBody, TimeRangeEditor } from './BlockConfirmBody.tsx';
import { formatListingRange } from './listing-zone-time.ts';
import { calendarInstruction } from '@client/shared/calendar/text.ts';
import type { SelectionResult } from '@client/shared/calendar/booking-grid.ts';
import type {
  BookingIntake,
  EditBounds,
  EditTimes,
  EditingFlow,
  SettledFlow,
} from './use-booking-intake.ts';
import styles from './ListingDetails.module.css';

export type SlotActionCardProps = NonNullable<BookingIntake['card']>;

export const SlotActionCard = forwardRef<HTMLDivElement, SlotActionCardProps>(function SlotActionCard(
  card,
  ref,
) {
  const titleId = useId();

  const { onClose } = card;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('[role="listbox"]')) return;
      e.preventDefault();
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <AnchoredCard
      ref={ref}
      anchor={{ point: card.anchor }}
      className={styles.actionCard}
      gap={8}
      cardWidth={336}
      flipRoom={260}
      flipUp={260}
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
    >
      <Stack gap={3}>
        {card.phase === 'settled' ? (
          <SuccessConfirmation flow={card.flow} zone={card.zone} titleId={titleId} onClose={onClose} />
        ) : (
          <EditingBody card={card} titleId={titleId} />
        )}
      </Stack>
    </AnchoredCard>
  );
});

function EditingBody({
  card,
  titleId,
}: {
  card: Extract<SlotActionCardProps, { phase: 'editing' }>;
  titleId: string;
}) {
  const [draft, setDraft] = useState('');
  const {
    zone,
    tapMode,
    mode,
    canToggleMode,
    onModeChange,
    selection,
    editTimes,
    editBounds,
    onAdjust,
    canBookForOthers,
    agents,
    viewerUserId,
    viewerBookable,
    assignee,
    needsAssignee,
    onAssigneeChange,
    onBook,
    submitting,
    blockRanges,
    onBlock,
    blocking,
    flow,
    onClose,
  } = card;
  const busy = submitting || blocking;

  return (
    <>
      {canToggleMode ? (
        <div className={styles.cardToggle}>
          <ModeToggle mode={mode} onSelect={onModeChange} disabled={busy} />
        </div>
      ) : (
        <Text
          id={titleId}
          as="span"
          size="xs"
          tone="muted"
          weight="semibold"
          className={styles.popoverEyebrow}
        >
          Book open house
        </Text>
      )}
      {canToggleMode && (
        <span id={titleId} className="sr-only">
          {mode === 'block' ? 'Block this time' : 'Book this open house'}
        </span>
      )}

      {mode === 'block' ? (
        <BlockBody
          blockRanges={blockRanges}
          zone={zone}
          editTimes={editTimes}
          editBounds={editBounds}
          onAdjust={onAdjust}
          draft={draft}
          onDraftChange={setDraft}
          busy={busy}
          conflict={flow.kind === 'conflict'}
          onCommit={() => onBlock(draft)}
          onCancel={onClose}
        />
      ) : (
        <BookBody
          selection={selection}
          zone={zone}
          tapMode={tapMode}
          editTimes={editTimes}
          editBounds={editBounds}
          onAdjust={onAdjust}
          canBookForOthers={canBookForOthers}
          agents={agents}
          viewerUserId={viewerUserId}
          viewerBookable={viewerBookable}
          assignee={assignee}
          needsAssignee={needsAssignee}
          onAssigneeChange={onAssigneeChange}
          flow={flow}
          busy={busy}
          submitting={submitting}
          onBook={onBook}
          onCancel={onClose}
        />
      )}
    </>
  );
}

function BlockBody({
  blockRanges,
  zone,
  editTimes,
  editBounds,
  onAdjust,
  draft,
  onDraftChange,
  busy,
  conflict,
  onCommit,
  onCancel,
}: {
  blockRanges: readonly { start: string; end: string }[];
  zone: string | null;
  editTimes: EditTimes | null;
  editBounds: EditBounds | null;
  onAdjust: (edge: 'start' | 'end', delta: -1 | 1) => void;
  draft: string;
  onDraftChange: (value: string) => void;
  busy: boolean;
  conflict: boolean;
  onCommit: () => void;
  onCancel: () => void;
}) {
  return (
    <BlockConfirmBody
      ranges={blockRanges}
      zone={zone}
      edit={editTimes && editBounds ? { times: editTimes, bounds: editBounds, onAdjust } : null}
      draft={draft}
      onDraftChange={onDraftChange}
      busy={busy}
      conflict={conflict}
      onCommit={onCommit}
      onCancel={onCancel}
    />
  );
}

function BookBody({
  selection,
  zone,
  tapMode,
  editTimes,
  editBounds,
  onAdjust,
  canBookForOthers,
  agents,
  viewerUserId,
  viewerBookable,
  assignee,
  needsAssignee,
  onAssigneeChange,
  flow,
  busy,
  submitting,
  onBook,
  onCancel,
}: {
  selection: SelectionResult;
  zone: string | null;
  tapMode: boolean;
  editTimes: EditTimes | null;
  editBounds: EditBounds | null;
  onAdjust: (edge: 'start' | 'end', delta: -1 | 1) => void;
  canBookForOthers: boolean;
  agents: readonly Agent[];
  viewerUserId: string | null;
  viewerBookable: boolean;
  assignee: string | null;
  needsAssignee: boolean;
  onAssigneeChange: (agentUserId: string | null) => void;
  flow: EditingFlow;
  busy: boolean;
  submitting: boolean;
  onBook: () => void;
  onCancel: () => void;
}) {
  const bookable = selection.kind === 'ok';
  return (
    <Stack gap={3}>
      {bookable && editTimes && editBounds ? (
        <TimeRangeEditor
          editTimes={editTimes}
          editBounds={editBounds}
          zone={zone}
          onAdjust={onAdjust}
          disabled={busy}
        />
      ) : bookable ? (
        <Text size="sm" weight="medium">
          <time className={cx(styles.bookingRange, 'tnum')}>
            {formatListingRange(selection.range.start, selection.range.end, zone)}
          </time>
        </Text>
      ) : (
        <SelectionHint selection={selection} tapMode={tapMode} />
      )}

      {canBookForOthers && (
        <AssigneeCombobox
          agents={agents}
          viewerUserId={viewerUserId}
          selfBookable={viewerBookable}
          value={assignee}
          onChange={onAssigneeChange}
          disabled={submitting}
        />
      )}

      {flow.kind !== 'idle' && <BookError flow={flow} />}

      <Row gap={2} align="center">
        <Button variant="ghost" size="sm" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
        <Spacer />
        <Button
          variant="primary"
          size="sm"
          disabled={!bookable || needsAssignee}
          loading={submitting}
          onClick={onBook}
        >
          Book this open house
        </Button>
      </Row>
    </Stack>
  );
}

function SelectionHint({
  selection,
  tapMode,
}: {
  selection: SelectionResult;
  tapMode: boolean;
}) {
  if (selection.kind === 'not_contiguous') {
    return (
      <Text size="sm" tone="danger">
        An open house is one continuous block: clear any gap or pick a single day.
      </Text>
    );
  }
  if (selection.kind === 'invalid') {
    return (
      <Text size="sm" tone="danger">
        That selection isn’t bookable. Try another time.
      </Text>
    );
  }
  return (
    <Text size="sm" tone="soft">
      {calendarInstruction(tapMode ? 'tap' : 'drag')}
    </Text>
  );
}

function BookError({ flow }: { flow: EditingFlow }) {
  if (flow.kind === 'slot_taken' || flow.kind === 'outside_window' || flow.kind === 'error') {
    return (
      <Text size="xs" tone="danger">
        {flow.message}
      </Text>
    );
  }
  return null;
}

function SuccessConfirmation({
  flow,
  zone,
  titleId,
  onClose,
}: {
  flow: SettledFlow;
  zone: string | null;
  titleId: string;
  onClose: () => void;
}) {
  return (
    <Stack gap={3}>
      <Row gap={2} align="center">
        <span className={styles.successCheck} aria-hidden="true">
          <CheckGlyph />
        </span>
        <Text id={titleId} size="sm" className={styles.bookingOk}>
          {flow.kind === 'booked' ? (
            <>
              {flow.forAgent
                ? `${flow.forAgent}’s open house is booked for `
                : 'Your open house is booked for '}
              <span className="tnum">{formatListingRange(flow.range.start, flow.range.end, zone)}</span>.
            </>
          ) : flow.kind === 'blocked' ? (
            flow.count === 1
              ? 'Time blocked: agents can no longer book this slot.'
              : `${flow.count} ranges blocked: agents can no longer book those slots.`
          ) : null}
        </Text>
      </Row>
      <Row align="center">
        <Spacer />
        <Button autoFocus variant="ghost" size="sm" onClick={onClose}>
          Done
        </Button>
      </Row>
    </Stack>
  );
}

function CheckGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path
        d="M3.5 8.5l2.8 2.8 6.2-6.6"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

